import classNames from 'clsx';
import { camelCase } from 'lodash';
import type { ButtonHTMLAttributes, DOMAttributes, ReactNode } from 'react';
import React from 'react';

import type { LinkProps } from '@swf/components/Link/Link';
import Link from '@swf/components/Link/Link';

import styles from './StarsButton.scss';
import Loader from './components/Loader/Loader';

export type Props = LinkProps &
  Partial<DOMAttributes<HTMLButtonElement> & ButtonHTMLAttributes<HTMLButtonElement>> & {
    block?: boolean;
    disabled?: boolean;
    icon?: boolean;
    inverse?: boolean;
    loading?: boolean;
    size?: 'extra-large' | 'large' | 'small' | 'tiny';
    title?: ReactNode | Element;
    variant?:
      | 'primary'
      | 'secondary'
      | 'tertiary'
      | 'casino'
      | 'casino-glass'
      | 'filter'
      | 'text'
      | 'legacy';
    routeValues?: { [key: string]: string };
  };

const getClassName = (props: Props) =>
  classNames(
    styles.button,
    styles[camelCase(props.size)],
    styles[camelCase(props.variant)],
    {
      [styles.block]: props.block,
      [styles.disabled]: typeof props.disabled === 'boolean' ? props.disabled : props.loading,
      [styles.loading]: props.loading,
      [styles.inverse]: props.inverse,
      [styles.icon]: props.icon
    },
    props.className
  );

const RawButton: React.FC<Props> = (props) => {
  const {
    block,
    loading,
    href,
    icon,
    route,
    size,
    disabled,
    variant,
    type,
    inverse,
    activeClassName,
    isActive,
    routeValues,
    ...rest
  } = props;
  const className = getClassName(props);
  const children = loading ? <Loader /> : rest.children;

  if (href || route) {
    return (
      <Link
        {...rest}
        href={href}
        route={route}
        values={routeValues}
        className={className}
        activeClassName={activeClassName}
        isActive={isActive}
        track={false}
      >
        {children}
      </Link>
    );
  }

  return (
    <button {...rest} type={type} disabled={disabled || loading} className={className}>
      {children}
    </button>
  );
};

RawButton.defaultProps = {
  size: 'large',
  type: 'button',
  variant: 'primary'
};

export default RawButton;
